/* eslint-disable @typescript-eslint/camelcase */
import { Grid, makeStyles, Typography } from '@material-ui/core';
import {
  CheckCircle as CheckCircleIcon,
  DeveloperBoard as DeveloperBoardIcon,
  Dvr as DvrIcon,
  SettingsSystemDaydream as SettingsSystemDaydreamIcon,
  SyncAlt as SyncAltIcon,
  Tune as TuneIcon,
  Update as UpdateIcon,
} from '@material-ui/icons';
import WarningOutlinedIcon from '@material-ui/icons/WarningOutlined';
import clsx from 'clsx';
import Breadcrumbs, { BreadcrumbsFields } from 'components/Breadcrumbs';
import EnvironmentDashboard from 'containers/Environments/EnvironmentDashboard';
import i18n from 'i18n';
import {
  cleanEnvironmentRequest,
  getEnvironmentRequest,
} from 'modules/environments/actions';
import {
  EnvironmentFields,
  EnvironmentStatus,
  ProviderResourcesProps,
} from 'modules/environments/interfaces';
import React, { memo } from 'react';
import { connect } from 'react-redux';
import { useParams, useHistory } from 'react-router';
import ApplicationState from 'store/interfaces';
import { messagesRequest } from 'modules/messages/actions';
import { PermissionsData } from 'modules/authenticate/interfaces';
import PageTitle from '../../../../components/PageTitle';
import NavBarEnvironments from '../../NavBarEnvironments';
import styles from './styles';

const useStyles = makeStyles(styles);

interface TemplateProps {
  currentPage?:
    | 'instances'
    | 'servers'
    | 'configuration'
    | 'maintenance'
    | 'history'
    | 'performance'
    | 'sessions';
  titlePage?: string;
  breadcrumbs?: BreadcrumbsFields[];
  titleContent?: string;
  subtitleContent?: string;
  actionsContent?: React.ReactNode;
  actionsHeader?: React.ReactNode;
  children?: React.ReactNode;
  environment: EnvironmentFields | null;
  permissions?: PermissionsData;
  providerResources: ProviderResourcesProps;
  isListContent?: boolean;
  cleanEnvironmentRequest: () => void;
  getEnvironmentRequest: (props: { id: string; skipProvider: boolean }) => void;
  messagesRequest: (props) => void;
}

const EnvironmentsTemplate = ({
  currentPage,
  titlePage,
  breadcrumbs,
  titleContent,
  subtitleContent,
  actionsContent,
  children,
  environment,
  actionsHeader,
  permissions,
  providerResources,
  isListContent,
  cleanEnvironmentRequest,
  getEnvironmentRequest,
  messagesRequest,
}: TemplateProps) => {
  const classes = useStyles();
  const history = useHistory();
  const { id, section } = useParams<{ id: string; section: string }>();
  const requiredRefresh =
    currentPage && ['configuration', 'maintenance'].includes(currentPage);

  React.useEffect(() => {
    if (!id) {
      cleanEnvironmentRequest();
      return;
    }

    if (environment?.environment_uuid !== id || requiredRefresh) {
      getEnvironmentRequest({
        id,
        skipProvider: providerResources?.environment_uuid === id,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const showMessage =
      id &&
      environment?.env_status === EnvironmentStatus.PENDING &&
      !!permissions?.can_manage_environment_status &&
      !!permissions?.edit_environment &&
      section !== 'status';
    if (showMessage) {
      messagesRequest({
        type: 'info',
        message: i18n.t('ENVIRONMENT_STATUS_NOTIFICATION_KEY'),
        action: () =>
          history.push(
            `/environments/${environment?.environment_uuid}/configuration/status`
          ),
        labelAction: i18n.t('ENVIRONMENT_STATUS_NOTIFICATION_ACTION_KEY'),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, environment]);

  const getEnvironmentStatusIcon = () => {
    const options = {
      [EnvironmentStatus.PENDING]: <UpdateIcon />,
      [EnvironmentStatus.DEMONSTRATION]: <DvrIcon />,
      [EnvironmentStatus.DEPLOYMENT]: <SettingsSystemDaydreamIcon />,
      [EnvironmentStatus.DEVELOPMENT]: <TuneIcon />,
      [EnvironmentStatus.HOMOLOGATION]: <DeveloperBoardIcon />,
      [EnvironmentStatus.MIGRATION]: <SyncAltIcon />,
      [EnvironmentStatus.PRODUCTION]: <CheckCircleIcon />,
    };
    return environment?.env_status
      ? options[environment.env_status]
      : options[EnvironmentStatus.PENDING];
  };

  return (
    <Grid container direction="column">
      <Grid container justify="space-between">
        <Grid
          container
          justify="flex-start"
          className={classes.environmentHead}
        >
          <PageTitle
            title={titlePage ?? `${i18n.t('LOADING_KEY')}...`}
            subtitle={breadcrumbs ? <Breadcrumbs values={breadcrumbs} /> : ''}
          />
          {actionsHeader}
          {environment?.in_maintenance && (
            <Grid item className={classes.maintenanceWarn}>
              <WarningOutlinedIcon
                color="error"
                className={classes.maintenanceIcon}
              />
              <Typography className={classes.maintenanceText}>
                {i18n.t('MAINTENANCE_WARNING_KEY')}
              </Typography>
            </Grid>
          )}
        </Grid>
        {environment && (
          <Grid item className={classes.status}>
            <Typography component="span" className="statusDescription">
              {`${i18n.t(
                `ENVIRONMENT_STATUS.${environment?.env_status?.toUpperCase()}_DESCRIPTION_KEY`
              )} -`}
            </Typography>
            <Typography component="span">
              {i18n.t(
                `ENVIRONMENT_STATUS.${environment?.env_status?.toUpperCase()}_KEY`
              )}
            </Typography>
            {getEnvironmentStatusIcon()}
          </Grid>
        )}
      </Grid>
      {currentPage && (
        <>
          <Grid container className={classes.environmentDashboard}>
            <EnvironmentDashboard environment={environment} />
          </Grid>
          <NavBarEnvironments environment={environment} current={currentPage} />
        </>
      )}

      <Grid
        className={clsx(classes.sectionContent, {
          [classes.sectionContentList]: isListContent,
        })}
      >
        <Grid container justify="space-between">
          <PageTitle
            title={titleContent ?? `${i18n.t('LOADING_KEY')}...`}
            subtitle={subtitleContent ?? `${i18n.t('LOADING_KEY')}...`}
          />
          {actionsContent}
        </Grid>

        {children}
      </Grid>
    </Grid>
  );
};

const mapStateToProps = ({
  environments: { environmentSelected, providerResources },
  authenticate: { data },
}: ApplicationState) => ({
  environment: environmentSelected,
  permissions: data?.permissions,
  providerResources,
});

export default connect(mapStateToProps, {
  cleanEnvironmentRequest,
  getEnvironmentRequest,
  messagesRequest,
})(memo(EnvironmentsTemplate));
